import type { HomeDeliverySetting } from 'models/ppoints/settings/HomeDeliverySetting';
import { queryClient } from 'utils/queryClient';
import { Aggregator } from '../../../../pb/aggregators/v1/aggregator_pb';
import { AVAILABLE_PROVIDERS, PackageProviderName, SettingKey } from '../../../data/ppoints/constants';
import { ShippingType } from '../../../features/orders/models/ShippingType';
import { ShopifyOrder } from '../../../features/orders/models/ShopifyOrder';
import OrderService from '../../../features/orders/services/OrderService';
import httpApi from '../../../hooks/api/httpApi';
import { Country, CountryWithProviders } from '../../../models/ppoints/Country';
import { CountryProviderShippingTypes } from '../../../models/ppoints/CountryProviderShippingTypes';
import { PackageProvider } from '../../../models/ppoints/PackageProvider';
import {
    EnabledShippingTypesSetting,
    ProviderToCountrySetting,
    SettingsToEcom,
} from '../../../models/ppoints/settings/SettingsToEcom';
import { ShippingRatesSettings } from '../../../models/ppoints/settings/ShippingRatesSettings';

/**
 * Redux Store has to be injected in index.tsx like this.
 * If the store was imported like usually, it would result in error.
 */
let store;
const injectStore = (_store) => {
    store = _store;
};

const getAll = async () => {
    const res = await httpApi.get<PackageProvider[]>('/api/package-providers');
    return res.getData();
};
const getAllCached = async (): Promise<PackageProvider[]> => {
    return await queryClient.ensureQueryData({
        queryKey: ['package-providers'],
        queryFn: getAll,
        staleTime: 10 * 60000,
        cacheTime: 10 * 60000,
    });
};

const getAvailableProvidersInAllOfTheCountries = (allCountries: CountryWithProviders[], countriesList: string[]) => {
    // we only need the countries whose names were passed in countriesList
    allCountries = allCountries.filter((country) =>
        countriesList.find((countryString) => countryString === country.name)
    );

    // Find first element of countriesList in allCountries
    let firstCountry = allCountries.find((country) => country.name === countriesList[0]);
    if (!firstCountry) {
        return [];
    }

    // Take the providers for that country
    let availableProviders = firstCountry.packageproviders;

    // Loop through rest of allCountries
    for (let i = 1; i < countriesList.length; i++) {
        // Find current country in allCountries
        let countryFound = allCountries.find((country) => country.name === countriesList[i]);
        if (!countryFound) {
            return [];
        }

        // Filter availableProviders: keep only providers which are also available in current country
        availableProviders = availableProviders.filter((provider) =>
            countryFound.packageproviders.find((provider2) => provider.name === provider2.name)
        );
    }

    return availableProviders;
};
const getAvailableProvidersInAnyOfTheCountries = (allCountries: CountryWithProviders[], countriesList: string[]) => {
    let availableProviders: PackageProvider[] = [];
    for (let i = 0; i < countriesList.length; i++) {
        let countryFound = allCountries.find((country) => countriesList.includes(country.name));
        if (!countryFound) {
            continue;
        }
        availableProviders = [...availableProviders, ...countryFound.packageproviders];
    }
    /* remove duplicates */
    availableProviders = availableProviders.filter(
        (provider, index) => availableProviders.findIndex((item) => item.name === provider.name) === index
    );
    return availableProviders;
};

const getById = async (id: number): Promise<PackageProvider | null> => {
    let all = await getAllCached();
    return all.find((provider) => provider.id === id) || null;
};

const getByName = async (name: PackageProviderName): Promise<PackageProvider | null> => {
    let all = await getAllCached();
    return all.find((provider) => provider.name === name) || null;
};

const isProviderEnabled = async (ecomId: number, name: string): Promise<boolean> => {
    const settings = store?.getState().settings.settings;
    if (!settings) return false;
    let providers = await getAllCached();
    const shippingZoneSetting = settings[SettingKey.zones_and_providers];

    // TODO group MPL providers
    switch (name) {
        case PackageProviderName.MPL_MOL:
        case PackageProviderName.MPL_COOP:
        case PackageProviderName.MPL_OFFICE:
        case PackageProviderName.MPL_MEDIAMARKT:
            name = PackageProviderName.MPL_PACKAGE_POINT;
            break;
    }
    if (name === PackageProviderName.MPL_DELIVERY) {
        return false;
    }

    const provider = providers.find((provider) => provider.name === name);
    if (!provider) return false;

    if (shippingZoneSetting && Object.keys(shippingZoneSetting).length > 0) {
        for (let zoneId of Object.keys(shippingZoneSetting)) {
            if (!!shippingZoneSetting[zoneId] && shippingZoneSetting[zoneId].includes(provider.id)) {
                return true;
            }
        }
    }

    return false;
};

const getProviderStates = async (ecomId: number | null): Promise<Record<PackageProviderName, boolean>> => {
    if (!ecomId) return Promise.reject();
    let providerStates = {} as Record<PackageProviderName, boolean>;
    for (let name in PackageProviderName) {
        providerStates[PackageProviderName[name]] = await isProviderEnabled(ecomId, PackageProviderName[name]);
    }
    return providerStates;
};

const getCountriesForProvider = async (providerId: number) => {
    const res = await httpApi.get<Country[]>(`/api/countries-for-provider/${providerId}`);
    const res2 = res.getData();
    return res2;
};

const getDisplayName = (providerName: PackageProviderName) => {
    switch (providerName) {
        case PackageProviderName.FOXPOST:
            return 'FOXPOST - Packeta Group';
        case PackageProviderName.MPL_PACKAGE_POINT:
            return 'Magyar Posta (MPL)';
        case PackageProviderName.MPL_DELIVERY:
            return 'MPL Delivery';
        case PackageProviderName.MPL_MOL:
            return 'MPL MOL';
        case PackageProviderName.MPL_COOP:
            return 'MPL COOP';
        case PackageProviderName.MPL_MEDIAMARKT:
            return 'MPL MediaMarkt';
        case PackageProviderName.MPL_OFFICE:
            return 'MPL Office';
        case PackageProviderName.GLS:
            return 'GLS';
        case PackageProviderName.DPD:
            return 'DPD';
        case PackageProviderName.SPRINTER:
            return 'Sprinter / Pick Pack Pont';
        case PackageProviderName.PACKETA:
            return 'Packeta';
        case PackageProviderName.TOURMIX:
            return 'TOURMIX';
        case PackageProviderName.FAN_COURIER:
            return 'FAN Courier';
        case PackageProviderName.FAN_COURIER_INTERNATIONAL:
            return 'FAN Courier International';
        case PackageProviderName.POSTASK:
            return 'Slovenská pošta';
        case PackageProviderName.BPOST:
            return 'BPost';
        case PackageProviderName.CORREOS:
            return 'Correos';
        case PackageProviderName.OMNIVA:
            return 'Omniva';
        case PackageProviderName.CARGUS:
            return 'Cargus';
        case PackageProviderName.KVIKK_GLS:
            return 'Kvikk GLS';
        case PackageProviderName.KVIKK_DPD:
            return 'Kvikk DPD';
        case PackageProviderName.KVIKK_PACKETA:
            return 'Kvikk Packeta';
        case PackageProviderName.KVIKK_MPL:
            return 'Kvikk MPL';
        case PackageProviderName.KVIKK_FOXPOST:
            return 'Kvikk Foxpost';

        default:
            return (providerName as string).toUpperCase();
    }
};

const getAggregatorDisplayName = (aggregatorName: Aggregator) => {
    switch (aggregatorName) {
        case Aggregator.KVIKK:
            return 'Kvikk';
        default:
            return Aggregator[aggregatorName] ?? '';
    }
};

const getProviderNameFromOrder = (order: ShopifyOrder) => {
    let shippingType = OrderService.getShippingType(order);
    if (!shippingType) {
        return null;
    }
    switch (shippingType) {
        case ShippingType.FOXPOST:
            return PackageProviderName.FOXPOST;
        case ShippingType.MPL_CS:
            return PackageProviderName.MPL_PACKAGE_POINT;
        case ShippingType.MPL_MOL:
            return PackageProviderName.MPL_MOL;
        case ShippingType.MPL_COOP:
            return PackageProviderName.MPL_COOP;
        case ShippingType.MPL_MEDIAMARKT:
            return PackageProviderName.MPL_MEDIAMARKT;
        case ShippingType.MPL_OFFICE:
            return PackageProviderName.MPL_OFFICE;
        case ShippingType.GLS:
            return PackageProviderName.GLS;
        case ShippingType.DPD:
            return PackageProviderName.DPD;
        default:
            return null;
    }
};

const getCountries = async () => {
    const res = await httpApi.get<CountryWithProviders[]>('/api/countries');
    return res.getData();
};

const getAvailableShippingTypes = async () => {
    const res = await httpApi.get<CountryProviderShippingTypes>('/api/available-shipping-types');
    return res.getData();
};

export const getAvailableProviderNames = async () => {
    let res = await httpApi.get<PackageProvider[]>(`/api/package-providers`);
    let data = res.getData();

    const providersEnabled: PackageProviderName[] = data
        .filter((provider) => provider.enabled)
        .map((provider) => provider.name);
    return providersEnabled;
};

/**
 * Returns count of enabled shipping types for package provider.
 * @param providerName
 * @param enabledShippingTypes
 */
const countEnabledShippingTypes = (
    providerName: PackageProviderName,
    enabledShippingTypes?: EnabledShippingTypesSetting
): number => {
    if (!enabledShippingTypes) {
        const settings = store.getState().settings.settings;
        enabledShippingTypes = settings[SettingKey.enabled_shipping_types];
    }
    const enabledShippingTypesByProvider = store.getState().enabledShippingTypes.enabledShippingTypes;

    let countTotal = 0;
    for (let countryCode in enabledShippingTypes) {
        let shippingTypesInCountry = enabledShippingTypes[countryCode]?.[providerName];
        if (!!shippingTypesInCountry && shippingTypesInCountry.length > 0) {
            countTotal += shippingTypesInCountry.length;
        }
    }

    if (enabledShippingTypesByProvider?.length > 0) {
        for (const enabledShippingType of enabledShippingTypesByProvider) {
            const providerData = enabledShippingType.providers.find((provider) => provider.name === providerName);
            if (providerData?.shippingTypes?.length) {
                countTotal += providerData.shippingTypes.length;
            }
        }
    }
    return countTotal;
};

/**
 * If package provider has home delivery enabled.
 * Determines by home_delivery setting
 * @param providerName
 * @param homeDelivery
 */
const isHomeDeliveryEnabled = (providerName: PackageProviderName, homeDelivery?: HomeDeliverySetting): boolean => {
    if (!homeDelivery) {
        const settings = store.getState().settings.settings;
        homeDelivery = settings[SettingKey.home_delivery];
    }
    return !!homeDelivery?.[providerName]?.home_delivery;
};

/**
 * Get array of enabled package provider names.
 * Determined by provider_to_country setting.
 * @param providerToCountrySetting
 */
const getProvidersEnabled = (providerToCountrySetting?: ProviderToCountrySetting | null): PackageProviderName[] => {
    if (!providerToCountrySetting) {
        const settings = store.getState().settings.settings;
        providerToCountrySetting = settings[SettingKey.provider_to_country];
    }
    let enabled: PackageProviderName[] = [];
    for (let countryName in providerToCountrySetting) {
        if (providerToCountrySetting[countryName]?.length > 0) {
            let providersInCountry: PackageProviderName[] = providerToCountrySetting[countryName];
            enabled.push(...providersInCountry);
        }
    }
    enabled = [...new Set(enabled)];
    return enabled;
};

const isProviderHDOnly = (provider: PackageProviderName, settings?: SettingsToEcom | null) => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    return (
        isHomeDeliveryEnabled(provider, settings?.[SettingKey.home_delivery]) &&
        countEnabledShippingTypes(provider, settings?.[SettingKey.enabled_shipping_types]) === 0
    );
};

const isProviderRateSelectRequired = (provider: PackageProviderName, settings?: SettingsToEcom | null) => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    return countEnabledShippingTypes(provider, settings?.[SettingKey.enabled_shipping_types]) > 0;
};

/**
 * Returns true if all package providers have no shipping types enabled (enabled_shipping_types setting)
 * AND home delivery is enabled.
 * @param settings
 */
const isAllProvidersHDOnly = (settings?: SettingsToEcom | null): boolean => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    let hdOnlyProvidersCount = 0;
    let providersEnabled = getProvidersEnabled(settings?.[SettingKey.provider_to_country]);
    for (let provider of providersEnabled) {
        if (isProviderHDOnly(provider, settings)) {
            hdOnlyProvidersCount++;
        }
    }
    return hdOnlyProvidersCount === providersEnabled.length;
};

/**
 * Get array of provider names for which at least one shipping type is enabled (on Package point tab, enabled_shipping_types setting)
 * @param settings
 */
const getProvidersWithPackagePointEnabled = (settings?: SettingsToEcom): PackageProviderName[] => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    const providersPPEnabled: PackageProviderName[] = [];
    for (let provider of AVAILABLE_PROVIDERS) {
        if (countEnabledShippingTypes(provider, settings?.[SettingKey.enabled_shipping_types])) {
            providersPPEnabled.push(provider);
        }
    }
    return providersPPEnabled;
};

const isShippingRateSavedForProvider = (
    provider: PackageProviderName,
    shippingRatesSetting?: ShippingRatesSettings
) => {
    if (!shippingRatesSetting) {
        const settings = store.getState().settings.settings;
        shippingRatesSetting = settings[SettingKey.shipping_methods];
    }

    let saved: boolean =
        shippingRatesSetting?.[`providers`] &&
        Object.prototype.hasOwnProperty.call(shippingRatesSetting?.[`providers`], `provider_${provider}`);
    if (
        Array.isArray(shippingRatesSetting?.[`providers`]?.[`provider_${provider}`]) &&
        shippingRatesSetting?.[`providers`]?.[`provider_${provider}`].length === 0
    ) {
        saved = false;
    }
    if (
        typeof shippingRatesSetting?.[`providers`]?.[`provider_${provider}`] === 'object' &&
        Object.keys(shippingRatesSetting?.[`providers`]?.[`provider_${provider}`]).length === 0
    ) {
        saved = false;
    }
    return saved;
};

export default {
    injectStore,
    getAllCached,
    getById,
    getByName,
    getAvailableProvidersInAllOfTheCountries,
    getAvailableProvidersInAnyOfTheCountries,
    isProviderEnabled,
    getProviderStates,
    getCountriesForProvider,
    getDisplayName,
    getAggregatorDisplayName,
    getProviderNameFromOrder,
    getCountries,
    getAvailableShippingTypes,
    countEnabledShippingTypes,
    isHomeDeliveryEnabled,
    getProvidersEnabled,
    isProviderHDOnly,
    isAllProvidersHDOnly,
    getProvidersWithPackagePointEnabled,
    isShippingRateSavedForProvider,
    isProviderRateSelectRequired,
};
